import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  capitalize,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Skeleton,
  Typography,
} from '@mui/material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import {
  CorrectIcon,
  PreviewIcon,
  CancelIcon,
  SubmitIcon,
  VerifyIcon,
} from 'src/v3/core/icons'
import axios, { pluckData } from 'src/utilities/http-client'
import { ComplianceProgress, ComplianceUserItemJoin } from 'src/v3/core/types'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { LightweightUser } from '../../manage.slice'
import { useNotifications } from '../../hooks/use-notifications'
import {
  fetchComplianceProgress,
  fetchComplianceUserItems,
  selectComplianceUserItems,
  selectComplianceUserItemsStatus,
} from '../../compliance.slice'
import { dataGridSx } from '../../styles'
import { sortComplianceItems } from '../types'
import RecertCycleDetailModal from './modals/recert-cycle-detail-modal'
import { DocumentPreviewDialog } from './document-preview-dialog'

type Props = {
  complianceID: string
  linkedServiceID: string | undefined
  progress: ComplianceProgress | null
  users: LightweightUser[]
  isLoading: boolean
}

type UserItemsProps = {
  userID: string
  complianceID: string
  linkedServiceID: string | undefined
  onProgressRefresh: () => void
}

function ItemRow({
  join,
  onVerify,
  onPreview,
  loadingPreview,
  onDetailClick,
}: {
  join: ComplianceUserItemJoin
  complianceID: string
  userID: string
  onVerify: (itemID: string, entityID: string, itemType: string) => void
  onPreview: (entityID: string) => void
  loadingPreview: string | null
  onDetailClick: (cycleID: string, cycleName: string | null) => void
}) {
  let isCompleted = join.status === 'accepted' || join.status === 'completed'

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        minHeight: 52,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 0 },
      }}
    >
      <Chip
        label={
          join.itemType === 'recert'
            ? 'Recertification'
            : join.itemType === 'nremt'
              ? 'NREMT'
              : capitalize(join.itemType)
        }
        variant="outlined"
        size="small"
        sx={{ flexShrink: 0 }}
      />
      <Typography variant="body2" sx={{ flex: 1 }} noWrap>
        {join.label}
      </Typography>

      <Chip
        label={join.status ? capitalize(join.status) : 'None'}
        color={(() => {
          let colorMap: Record<
            string,
            'success' | 'error' | 'warning' | 'primary' | 'default'
          > = {
            accepted: 'success',
            completed: 'success',
            rejected: 'error',
            pending: 'warning',
            started: 'primary',
          }
          return join.status ? (colorMap[join.status] ?? 'default') : 'default'
        })()}
        size="small"
      />

      {(join.itemType === 'license' ||
        join.itemType === 'credential' ||
        join.itemType === 'nremt') &&
        isCompleted && (
          <Box className="flex items-center gap-1">
            <CorrectIcon sx={{ fontSize: 16, color: 'success.main' }} />
            <Typography variant="body2" sx={{ color: 'success.main' }}>
              Verified
            </Typography>
          </Box>
        )}

      {(join.itemType === 'license' ||
        join.itemType === 'credential' ||
        join.itemType === 'nremt') &&
        join.entityID && (
          <Button
            size="small"
            variant="outlined"
            startIcon={
              loadingPreview === join.entityID ? (
                <CircularProgress size={14} />
              ) : (
                <PreviewIcon />
              )
            }
            disabled={loadingPreview === join.entityID}
            onClick={() => onPreview(join.entityID!)}
          >
            View
          </Button>
        )}

      {(join.itemType === 'license' ||
        join.itemType === 'credential' ||
        join.itemType === 'nremt') &&
        !isCompleted &&
        join.entityID && (
          <Button
            size="small"
            variant="contained"
            startIcon={<VerifyIcon />}
            onClick={() =>
              onVerify(join.complianceItemID, join.entityID!, join.itemType)
            }
          >
            Review
          </Button>
        )}

      {join.itemType === 'recert' && join.entityID && (
        <Button
          size="small"
          variant="outlined"
          onClick={() => onDetailClick(join.entityID!, join.entityLabel)}
        >
          Detail
        </Button>
      )}
    </Box>
  )
}

function UserItemsPanel({
  userID,
  complianceID,
  linkedServiceID,
  onProgressRefresh,
}: UserItemsProps) {
  let dispatch = useAppDispatch()
  let items = sortComplianceItems(
    useAppSelector(selectComplianceUserItems(complianceID, userID)) ?? [],
  )
  let itemsStatus = useAppSelector(
    selectComplianceUserItemsStatus(complianceID, userID),
  )
  let loading = itemsStatus === 'pending' || itemsStatus === undefined
  let [loadingPreview, setLoadingPreview] = useState<string | null>(null)
  let [viewBlob, setViewBlob] = useState<Blob | null>(null)
  // The presigned URL's path ends in the stored file name, used to detect PDFs
  // served without a usable content type.
  let [viewFileName, setViewFileName] = useState<string>()
  let [verifyTarget, setVerifyTarget] = useState<{
    complianceItemID: string
    entityID: string
    itemType: string
  } | null>(null)
  let [verifyDecision, setVerifyDecision] = useState<'accepted' | 'rejected'>(
    'accepted',
  )
  let [saving, setSaving] = useState(false)
  let [detailCycleID, setDetailCycleID] = useState<string | null>(null)
  let [detailCycleName, setDetailCycleName] = useState<string | null>(null)

  let { showError, NotificationComponent } = useNotifications()

  let refreshItems = useCallback(() => {
    dispatch(fetchComplianceUserItems({ complianceID, userID }))
  }, [dispatch, complianceID, userID])

  useEffect(() => {
    if (!itemsStatus || itemsStatus === 'uninitialized') {
      refreshItems()
    }
  }, [itemsStatus, refreshItems])

  let handleSubmitVerify = useCallback(() => {
    if (!verifyTarget || !linkedServiceID) return
    setSaving(true)
    let endpoint =
      verifyTarget.itemType === 'license'
        ? '/api/manage/license/compliance-verify'
        : verifyTarget.itemType === 'nremt'
          ? '/api/manage/nremt/compliance-verify'
          : '/api/manage/credential/compliance-verify'
    let payload =
      verifyTarget.itemType === 'license'
        ? {
            licenseID: verifyTarget.entityID,
            userID,
            linkedServiceID,
            status: verifyDecision,
          }
        : verifyTarget.itemType === 'nremt'
          ? {
              userID,
              linkedServiceID,
              status: verifyDecision,
            }
          : {
              credentialID: verifyTarget.entityID,
              linkedServiceID,
              status: verifyDecision,
            }
    axios
      .post(endpoint, payload)
      .then(() => {
        setVerifyTarget(null)
        setVerifyDecision('accepted')
        onProgressRefresh()
        refreshItems()
      })
      .catch((err) =>
        showError(err?.response?.data || err?.message || 'Verification failed'),
      )
      .finally(() => setSaving(false))
  }, [
    verifyTarget,
    verifyDecision,
    userID,
    linkedServiceID,
    onProgressRefresh,
    refreshItems,
    showError,
  ])

  let handlePreview = useCallback(
    (entityID: string, itemType: string) => {
      let imageUrl =
        itemType === 'credential'
          ? `/api/v2/manage/credential/image-url?CredentialID=${entityID}`
          : `/api/manage/${itemType}/image/${entityID}`
      setLoadingPreview(entityID)
      axios
        .get<string>(imageUrl)
        .then(pluckData)
        .then((url) => {
          setViewFileName(url.split('?')[0])
          return axios.get<Blob>(url, { responseType: 'blob' })
        })
        .then(pluckData)
        .then((blob) => setViewBlob(blob))
        .catch((err) =>
          showError(
            err?.response?.data || err?.message || 'No image available',
          ),
        )
        .finally(() => setLoadingPreview(null))
    },
    [showError],
  )

  if (loading) {
    return (
      <Box className="flex justify-center py-4">
        <CircularProgress size={24} />
      </Box>
    )
  }

  return (
    <>
      <Box
        sx={{
          m: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {items.map((join) => (
          <ItemRow
            key={join.complianceItemID}
            join={join}
            complianceID={complianceID}
            userID={userID}
            onVerify={(itemID, entityID, itemType) =>
              setVerifyTarget({ complianceItemID: itemID, entityID, itemType })
            }
            onPreview={(entityID) => handlePreview(entityID, join.itemType)}
            loadingPreview={loadingPreview}
            onDetailClick={(cycleID, cycleName) => {
              setDetailCycleID(cycleID)
              setDetailCycleName(cycleName ?? null)
            }}
          />
        ))}
        {items.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No items found.
          </Typography>
        )}
      </Box>

      <DocumentPreviewDialog
        blob={viewBlob}
        fileName={viewFileName}
        onClose={() => setViewBlob(null)}
      />

      <Dialog
        open={!!verifyTarget}
        onClose={() => {
          setVerifyTarget(null)
          setVerifyDecision('accepted')
        }}
      >
        <Paper sx={{ width: 300, px: 2, py: 1 }}>
          <DialogTitle sx={{ textAlign: 'center', mb: -1 }}>
            Verify{' '}
            {verifyTarget
              ? verifyTarget.itemType === 'nremt'
                ? 'NREMT'
                : capitalize(verifyTarget.itemType)
              : ''}
          </DialogTitle>
          <DialogContent
            sx={{
              px: 2,
              pb: 1,
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
            }}
          >
            <FormControl>
              <Typography color="textSecondary" sx={{ ml: -1, my: 0.5 }}>
                Decision
              </Typography>
              <RadioGroup
                value={verifyDecision}
                onChange={(e) =>
                  setVerifyDecision(e.target.value as 'accepted' | 'rejected')
                }
              >
                <FormControlLabel
                  value="accepted"
                  control={<Radio sx={{ height: 32, width: 32 }} />}
                  label="Accept"
                />
                <FormControlLabel
                  value="rejected"
                  control={<Radio sx={{ height: 32, width: 32 }} />}
                  label="Reject"
                />
              </RadioGroup>
            </FormControl>
          </DialogContent>
          <DialogActions
            sx={{ display: 'flex', justifyContent: 'center', pb: 1.5 }}
          >
            <Button
              color="primary"
              variant="contained"
              sx={{ flex: '1 1 0px' }}
              startIcon={<SubmitIcon />}
              loading={saving}
              onClick={handleSubmitVerify}
            >
              Submit
            </Button>
            <Button
              color="error"
              variant="contained"
              sx={{ flex: '1 1 0px' }}
              startIcon={<CancelIcon />}
              disabled={saving}
              onClick={() => {
                setVerifyTarget(null)
                setVerifyDecision('accepted')
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Paper>
      </Dialog>

      <RecertCycleDetailModal
        cycleID={detailCycleID}
        cycleName={detailCycleName}
        onClose={() => {
          setDetailCycleID(null)
          setDetailCycleName(null)
        }}
      />

      {NotificationComponent}
    </>
  )
}

function SkeletonCard() {
  return (
    <Card className="p-6 mb-6">
      <Skeleton variant="text" width={220} height={28} className="mb-4" />
      {[0, 1, 2].map((i) => (
        <Skeleton
          key={i}
          variant="rectangular"
          height={56}
          sx={{ borderRadius: 1, mb: 1 }}
        />
      ))}
    </Card>
  )
}

export default function UsersAccordionCard({
  complianceID,
  linkedServiceID,
  progress,
  users,
  isLoading,
}: Props) {
  let dispatch = useAppDispatch()
  let [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set())
  let userMap = useMemo(() => new Map(users.map((u) => [u.id, u])), [users])
  let rows = useMemo(
    () =>
      (progress?.users ?? [])
        .map((u) => {
          let user = userMap.get(u.userID)
          return {
            id: u.userID,
            userID: u.userID,
            name: user ? `${user.firstName} ${user.lastName}` : u.userID,
            lastName: user?.lastName ?? '',
            email: user?.primaryEmail ?? '',
            completedItems: u.completedItems,
            totalItems: u.totalItems,
            isComplete: u.isComplete,
            startedItems: u.startedItems,
          }
        })
        .toSorted(
          (a, b) =>
            a.lastName.localeCompare(b.lastName) ||
            a.name.localeCompare(b.name),
        ),
    [progress?.users, userMap],
  )

  let columns = useMemo<GridColDef[]>(
    () => [
      { field: 'name', headerName: 'User', flex: 1, minWidth: 160 },
      { field: 'email', headerName: 'Email', flex: 1, minWidth: 180 },
      {
        field: 'completedItems',
        headerName: 'Progress',
        width: 100,
        renderCell: (params) => (
          <Box className="flex items-center h-full">
            <Typography variant="body2">
              {params.row.completedItems}/{params.row.totalItems}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'isComplete',
        headerName: 'Status',
        width: 130,
        renderCell: (params) => (
          <Box className="flex items-center h-full">
            <Chip
              label={
                params.value
                  ? 'Complete'
                  : params.row.startedItems > 0
                    ? 'In Progress'
                    : 'Not Started'
              }
              color={
                params.value
                  ? 'success'
                  : params.row.startedItems > 0
                    ? 'primary'
                    : 'default'
              }
              size="small"
            />
          </Box>
        ),
      },
      {
        field: 'items',
        headerName: 'Items',
        width: 100,
        sortable: false,
        renderCell: (params) => (
          <Box className="flex items-center h-full">
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                setExpandedRowIds((prev) =>
                  prev.has(params.row.userID)
                    ? new Set()
                    : new Set([params.row.userID]),
                )
              }
            >
              {expandedRowIds.has(params.row.userID) ? 'Close' : 'Items'}
            </Button>
          </Box>
        ),
      },
    ],
    [expandedRowIds],
  )

  if (isLoading || !progress) return <SkeletonCard />

  let handleProgressRefresh = () => {
    dispatch(fetchComplianceProgress(complianceID))
  }

  return (
    <Card className="p-6 mb-6">
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }} className="mb-4">
        Progress by User
      </Typography>

      <DataGridPremium
        rows={rows}
        columns={columns}
        autoHeight
        disableRowSelectionOnClick
        pagination
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        detailPanelExpandedRowIds={expandedRowIds}
        onDetailPanelExpandedRowIdsChange={(ids) =>
          setExpandedRowIds(new Set(Array.from(ids).slice(-1) as string[]))
        }
        getDetailPanelContent={({ row }) => (
          <UserItemsPanel
            userID={row.userID}
            complianceID={complianceID}
            linkedServiceID={linkedServiceID}
            onProgressRefresh={handleProgressRefresh}
          />
        )}
        getDetailPanelHeight={() => 'auto'}
        sx={dataGridSx}
      />
    </Card>
  )
}
